import { type Key, type MouseEvent, type ReactElement } from 'react';
import { s } from '@/theme/tokens';
import { Box, CloseIcon, Text } from '@/ui/primitives';
import { TagChip } from '../tags/TagChip';
import { tagsFilterSelectedChipSx } from './TagsFilterFieldOption.styles';
import type { StatusTone, SvgIconComponent } from '@/ui/primitives';
import type { Theme } from '@mui/material/styles';

type TagsFilterFieldSelectedValueOption = {
  value: string;
  label: string;
  countIcon?: SvgIconComponent;
  countIconColor?: string | ((theme: Theme) => string);
  countIconSizePx?: number;
  color?: StatusTone;
};

type TagsFilterFieldSelectedValueProps = {
  controlHeight: number;
  getSelectedItemProps?: (input: { index: number }) => {
    className?: string;
    key?: Key;
  };
  inputLeftPadding: string;
  onRemoveValue?: (value: string) => void;
  selectedValues: TagsFilterFieldSelectedValueOption[];
  showOptionChips: boolean;
};

export const TagsFilterFieldSelectedValue = ({
  controlHeight,
  getSelectedItemProps,
  inputLeftPadding,
  onRemoveValue,
  selectedValues,
  showOptionChips,
}: TagsFilterFieldSelectedValueProps): ReactElement | null => {
  if (selectedValues.length === 0) return null;

  const shouldRenderSelectedIcons = selectedValues.some((option) => option.countIcon !== undefined);
  const shouldRenderSelectedChips = showOptionChips && !shouldRenderSelectedIcons;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: s('sm'),
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        flex: '1 1 auto',
        pl: inputLeftPadding,
        pr: s('none'),
        color: 'text.primary',
        font: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: `${controlHeight}px`,
        whiteSpace: 'nowrap',
      }}
    >
      {shouldRenderSelectedIcons ? (
        selectedValues.map((option) => {
          const SelectedIcon = option.countIcon;
          const iconSizePx = option.countIconSizePx ?? 16;
          return (
            <Box
              key={option.value}
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: s('sm'),
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              {SelectedIcon ? (
                <SelectedIcon
                  aria-hidden="true"
                  fontSize="small"
                  sx={{
                    width: `${iconSizePx}px`,
                    height: `${iconSizePx}px`,
                    fontSize: `${iconSizePx}px`,
                    flex: '0 0 auto',
                    color: option.countIconColor ?? 'text.secondary',
                  }}
                />
              ) : null}
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Box>
            </Box>
          );
        })
      ) : shouldRenderSelectedChips ? (
        selectedValues.map((option, index) => {
          const selectedItemProps = getSelectedItemProps?.({ index });
          const removeButton = onRemoveValue ? (
            <Box
              component="button"
              type="button"
              aria-label={`Remove ${option.label}`}
              data-flowqa-tag-chip-remove="true"
              onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                onRemoveValue(option.value);
              }}
              sx={{
                alignItems: 'center',
                appearance: 'none',
                background: 'transparent',
                border: 0,
                color: 'inherit',
                cursor: 'pointer',
                display: 'inline-flex',
                flex: '0 0 auto',
                justifyContent: 'center',
                ml: s('xs'),
                p: s('none'),
                '& .MuiSvgIcon-root': {
                  fontSize: '1em',
                },
              }}
            >
              <CloseIcon fontSize="inherit" />
            </Box>
          ) : null;

          return (
            <TagChip
              key={selectedItemProps?.key ?? option.value}
              className={selectedItemProps?.className}
              tone={option.color ?? 'neutral'}
              label={
                removeButton ? (
                  <Box
                    component="span"
                    sx={{
                      alignItems: 'center',
                      display: 'inline-flex',
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Text
                      role="body"
                      color="inherit"
                      component="span"
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {option.label}
                    </Text>
                    {removeButton}
                  </Box>
                ) : (
                  option.label
                )
              }
              sx={tagsFilterSelectedChipSx}
            />
          );
        })
      ) : (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            minWidth: 0,
            flex: '0 1 auto',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedValues.map((option) => option.label).join(', ')}
        </Box>
      )}
    </Box>
  );
};
